import { useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react'
import type { Placement } from '@floating-ui/react'
import { useDropdownReference } from '@/components/ui/Dropdown/components/DropdownWrapper/DropdownWrapper'
import css from './Dropdown.module.css'

interface DropdownProps {
    isOpen: boolean
    onClose: () => void
    children: ReactNode
    align?: 'left' | 'right'
    direction?: 'down' | 'up'
    width?: 'small' | 'medium'
    className?: string
}

const Dropdown = ({ isOpen, onClose, children, align = 'left', direction = 'down', width = 'medium', className }: DropdownProps) => {
    const referenceEl = useDropdownReference()
    const dropdownRef = useRef<HTMLDivElement | null>(null)

    const placement: Placement = `${direction === 'up' ? 'top' : 'bottom'}-${align === 'right' ? 'end' : 'start'}`

    const { refs, floatingStyles } = useFloating({
        placement,
        elements: { reference: referenceEl },
        middleware: [offset(8), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    })

    const setRefs = useCallback((node: HTMLDivElement | null) => {
        dropdownRef.current = node
        refs.setFloating(node)
    }, [refs])

    useEffect(() => {
        if (!isOpen) return

        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                onClose()
            }
        }

        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside)
        }, 0)

        return () => {
            clearTimeout(timeoutId)
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen, onClose])

    if (!isOpen) return null

    const dropdownClass = [
        css.dropdown,
        width === 'small' && css.dropdown__small,
        className
    ].filter(Boolean).join(' ')

    return createPortal(
        <div ref={setRefs} className={dropdownClass} style={floatingStyles} data-dropdown-portal="true">
            {children}
        </div>,
        document.body
    )
}

export default Dropdown
